import { useState, useEffect, useCallback } from 'react'
import { SupabaseClient } from '@supabase/supabase-js'
import type { Recall, Brand } from '../types'

interface UseRecallsOptions {
  page?: number
  limit?: number
  search?: string
  status?: 'active' | 'resolved' | 'expired'
  severity?: 'low' | 'medium' | 'high' | 'critical'
}

export function useRecalls(client: SupabaseClient | null, options: UseRecallsOptions = {}) {
  const { page = 1, limit = 20, search = '', status, severity } = options

  const [recalls, setRecalls] = useState<Recall[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchRecalls = useCallback(async () => {
    if (!client) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      let query = client
        .from('recalls')
        .select('*', { count: 'exact' })

      // Apply search filter
      if (search) {
        query = query.or(`title.ilike.%${search}%,brand_name.ilike.%${search}%`)
      }

      // Apply status filter
      if (status) {
        query = query.eq('status', status)
      }

      // Apply severity filter
      if (severity) {
        query = query.eq('severity', severity)
      }

      // Apply pagination
      const from = (page - 1) * limit
      const to = from + limit - 1
      query = query.range(from, to).order('published_at', { ascending: false, nullsFirst: false })

      const { data, error: queryError, count } = await query

      if (queryError) throw queryError

      setRecalls(data || [])
      setTotal(count || 0)
    } catch (err: any) {
      console.error('Error fetching recalls:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [client, page, limit, search, status, severity])

  useEffect(() => {
    fetchRecalls()
  }, [fetchRecalls])

  return { recalls, total, isLoading, error, refetch: fetchRecalls }
}

export function useRecallMutations(client: SupabaseClient | null) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createRecall = useCallback(async (recall: Partial<Recall>) => {
    if (!client) {
      console.error('No Supabase client available')
      return null
    }

    setIsLoading(true)
    setError(null)

    try {
      console.log('Creating recall with data:', recall)
      const { data, error: insertError } = await client
        .from('recalls')
        .insert(recall)
        .select()
        .single()

      if (insertError) {
        console.error('Supabase insert error:', insertError)
        throw insertError
      }
      console.log('Recall created successfully:', data)
      return data
    } catch (err: any) {
      console.error('Error creating recall:', err)
      setError(err.message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [client])

  const updateRecall = useCallback(async (id: string, updates: Partial<Recall>) => {
    if (!client) return null

    setIsLoading(true)
    setError(null)

    try {
      const { data, error: updateError } = await client
        .from('recalls')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()

      if (updateError) throw updateError
      return data
    } catch (err: any) {
      setError(err.message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [client])

  const deleteRecall = useCallback(async (id: string) => {
    if (!client) return false

    setIsLoading(true)
    setError(null)

    try {
      const { error: deleteError } = await client
        .from('recalls')
        .delete()
        .eq('id', id)

      if (deleteError) throw deleteError
      return true
    } catch (err: any) {
      setError(err.message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [client])

  return { createRecall, updateRecall, deleteRecall, isLoading, error }
}

interface BrandOrCompanyResult {
  id: string
  brand_name: string
  ownership_type: string | null
  type: 'brand' | 'company'
}

export function useBrandSearch(client: SupabaseClient | null, searchTerm: string) {
  const [results, setResults] = useState<BrandOrCompanyResult[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!client || !searchTerm || searchTerm.length < 2) {
      setResults([])
      return
    }

    const search = async () => {
      setIsLoading(true)
      try {
        // Search brands (excluding merged brands)
        const brandsPromise = client
          .from('brands')
          .select('id, brand_name, ownership_type')
          .ilike('brand_name', `%${searchTerm}%`)
          .is('merged_into_brand_id', null)
          .limit(10)

        // Search companies (no merged_into column in this table)
        const companiesPromise = client
          .from('companies')
          .select('id, company_name, ownership_type')
          .ilike('company_name', `%${searchTerm}%`)
          .limit(10)

        const [brandsResult, companiesResult] = await Promise.all([brandsPromise, companiesPromise])

        const brandResults: BrandOrCompanyResult[] = (brandsResult.data || []).map(b => ({
          id: b.id,
          brand_name: b.brand_name,
          ownership_type: b.ownership_type,
          type: 'brand' as const,
        }))

        // Handle companies query error gracefully (table might not exist or have different schema)
        const companyResults: BrandOrCompanyResult[] = (companiesResult.data || []).map(c => ({
          id: c.id,
          brand_name: c.company_name,
          ownership_type: c.ownership_type,
          type: 'company' as const,
        }))

        // Combine and sort alphabetically
        const combined = [...brandResults, ...companyResults].sort((a, b) =>
          a.brand_name.localeCompare(b.brand_name)
        )

        setResults(combined.slice(0, 20))
      } catch (err) {
        console.error('Error searching brands/companies:', err)
        setResults([])
      } finally {
        setIsLoading(false)
      }
    }

    const debounce = setTimeout(search, 300)
    return () => clearTimeout(debounce)
  }, [client, searchTerm])

  return { brands: results, isLoading }
}
